import React, { useEffect } from 'react'
import Translations from '@helpers/translations'
import { analytics } from '@helpers/analytics'
import { Button } from '@components/ui/Button'
import ModalContainer from '@components/ui/Modal/ModalContainer'
import styles from './styles.module.scss'
import useContentfulImage from '@helpers/hooks/useContentfulImage'
import Image from '@components/ui/Image'
import { TextareaField } from '@components/ui/formElements/form/Textarea'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

const TEXTAREA_MAX_LENGTH = 200
const GIFT_MESSAGE_CONTENTFUL_ID = '1BTgPVE9FFlTjvQy7viii1'

type GiftMessageProps = {
  onSubmit: (currentGiftMessage: string) => void
  giftMessage?: string
  onClose: () => void
}

function sendGiftMessageAnalytics(label: string) {
  analytics('Click', {
    category: 'Cart',
    label: `Gift message - ${label}`
  })
}

const GiftMessageFormSchema = z.object({
  giftMessageTextarea: TextareaField.getSchema(false, TEXTAREA_MAX_LENGTH)
})

type GiftMessageFormValues = z.infer<typeof GiftMessageFormSchema>

export default function GiftMessage({
  onSubmit,
  giftMessage = '',
  onClose
}: GiftMessageProps) {
  const methods = useForm<GiftMessageFormValues>({
    resolver: zodResolver(GiftMessageFormSchema),
    defaultValues: { giftMessageTextarea: giftMessage },
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all'
  })

  const { watch, handleSubmit, formState } = methods
  const messageValue = watch('giftMessageTextarea')

  const image = useContentfulImage(GIFT_MESSAGE_CONTENTFUL_ID)

  useEffect(() => {
    sendGiftMessageAnalytics('Opened')
    return () => sendGiftMessageAnalytics('Closed')
  }, [])

  const submitForm = handleSubmit(values => {
    if (values.giftMessageTextarea) {
      sendGiftMessageAnalytics(values.giftMessageTextarea ? 'Edited' : 'Saved')
      onSubmit(values.giftMessageTextarea)
    }
  })

  const isSubmitDisabled = !formState.isValid || !messageValue?.trim()

  return (
    <ModalContainer onClose={onClose} classes={styles.GiftMessage}>
      <h2>Gift Message</h2>
      <FormProvider {...methods}>
        <form
          className={`form ${styles.GiftMessage__form}`}
          method="post"
          onSubmit={submitForm}
          noValidate
        >
          <div className={styles.GiftMessage__textareaWrapper}>
            <Image alt="gift message" src={image} width="678" height="1054" />

            <div className={styles.GiftMessageTextarea}>
              <TextareaField
                id="giftMessageTextarea"
                name="Add your gift message"
                placeholder="Type your message here, but avoid emojis"
                maxLength={TEXTAREA_MAX_LENGTH}
                textPosition="center"
              />
            </div>
          </div>

          <div className={styles.GiftMessage__buttons}>
            <Button
              type="submit"
              variant="primary"
              fullWidth="always"
              disabled={isSubmitDisabled}
            >
              {Translations.localise('product.personalisation.save')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              fullWidth="always"
              onClick={onClose}
            >
              {Translations.localise('product.personalisation.cancel')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </ModalContainer>
  )
}
